import { Box, Button, IconButton, makeStyles, Paper, Typography } from "@material-ui/core";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import { ethers } from "ethers";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import LendingPoolABI from "src/assets/abis/poolMonitoring/LendingPool.json";
import CssGroupButton from "src/components/Button/CssGroupButton";
import ExternalLinkButton from "src/components/Button/ExternalLinkButton";
import ScoringToggleButton from "src/components/Button/ScoringToggleButton";
import ArrowAnimationIcon from "src/components/Icon/ArrowAnimationIcon";
import { FS } from "src/config/constants";
import { usePoolMonitoringContext } from "src/context/monitoring/poolMonitoringContext";
import { useProviderContext } from "src/ether-utils/context/providerContext";
import { encodePackageData, getContractEditPool } from "src/hooks/useContractEditPool";
import useLowerAddressParam from "src/hooks/useLowerAddressParam";
import useNotify from "src/hooks/useNotify";
import useQueryUrl from "src/hooks/useQueryUrl";
import PoolListPopover from "./PoolListPopover";

const useStyle = makeStyles((theme) => ({
  boxRoot: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  boxRoot1: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      marginTop: "1rem",
    },
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      justifyContent: "center",
      margin: "auto",
    },
  },
  toggleBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    [theme.breakpoints.up("sm")]: {
      marginRight: "1rem",
    },
    [theme.breakpoints.down("xs")]: {
      marginTop: "0.5rem",
      marginBottom: "0.5rem",
    },
  },
}));

export default function PoolInfo() {
  const cls = useStyle();
  const { pool } = useLowerAddressParam();
  const { action } = useQueryUrl();
  const { wallet, currentProvider } = useProviderContext();
  const { data, fetchingDataStatus, fetchPoolData } = usePoolMonitoringContext();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;
  const history = useHistory();
  const [isPoolPause, setPoolPause] = useState();
  const [loadingPoolPause, setLoadingPoolPause] = React.useState(false);
  const { errorNotify, successNotify } = useNotify();

  const poolList = useMemo(() => {
    if (fetchingDataStatus === FS.SUCCESS) {
      return Object.keys(data).map((poolAddress, _) => {
        return {
          name: data[poolAddress]["overview"].name,
          address: poolAddress,
          providerId: data[poolAddress]["overview"].providerId,
        };
      });
    }
    return [];
  }, [data, fetchingDataStatus]);

  useEffect(async () => {
    if (pool) {
      try {
        setLoadingPoolPause(true);
        const LendingPoolContract = new ethers.Contract(pool, LendingPoolABI, wallet.signer);
        const isPause = await LendingPoolContract.paused();
        setPoolPause(isPause);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingPoolPause(false);
      }
    }
  }, [pool]);

  const handlePausePool = async () => {
    if (pool && !loadingPoolPause) {
      try {
        setLoadingPoolPause(true);
        const dataEncode = await encodePackageData(currentProvider, "setPoolPause", [!isPoolPause]);
        const contractEditPool = getContractEditPool(wallet.signer);
        const _promise = await contractEditPool.submitTransaction(data[pool].overview.providerId, dataEncode);
        await _promise.wait();
        fetchPoolData();
        successNotify("Transaction successful");
        history.push(`/pool-monitoring/${pool}?action=pending`);
      } catch (e) {
        errorNotify(e.message);
      } finally {
        setLoadingPoolPause(false);
      }
    }
  };

  const name = useMemo(() => {
    if (data[pool] && data[pool]["overview"]["name"] !== "noname") return data[pool]["overview"]["name"];
    else return "Lending pool";
  }, [data, pool]);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  function onAddAssetClick() {
    history.push(`/pool-monitoring/${pool}?action=add-asset`);
  }

  return (
    <>
      {Object.keys(data).length > 0 && (
        <Paper style={{ marginTop: "1rem" }}>
          <Box className={cls.boxRoot}>
            <Box>
              <Typography color="textSecondary">Pool</Typography>
              <Box display="flex" justifyContent="flex-start" alignItems="center">
                {pool && (
                  <>
                    <Typography
                      color="textSecondary"
                      style={{ fontSize: "28px", fontWeight: 500, marginRight: "0.5rem" }}
                    >
                      {name}
                    </Typography>
                    <ExternalLinkButton hash={pool} config={{ pool: "trava-bsc" }} />
                    <IconButton onClick={(e) => handleClick(e)}>
                      <ArrowAnimationIcon isTransform={open} style={{ cursor: "pointer", fontSize: "20px" }} />
                    </IconButton>
                  </>
                )}
              </Box>
            </Box>
            <Box className={cls.boxRoot1}>
              <Box className={cls.toggleBox}>
                <Typography className={cls.hint}>Pause Pool</Typography>
                <ScoringToggleButton
                  isOn={isPoolPause}
                  disabled={loadingPoolPause || !pool}
                  loading={loadingPoolPause}
                  handleClick={() => handlePausePool()}
                  ml={1}
                  mr={2}
                />
              </Box>
              <Button
                variant="outlined"
                color="primary"
                disabled={!pool && fetchingDataStatus === FS.SUCCESS}
                onClick={() => onAddAssetClick()}
              >
                <AddCircleIcon style={{ width: 20, height: 20, marginRight: 8 }} />
                Add asset
              </Button>
            </Box>
          </Box>
        </Paper>
      )}
      {fetchingDataStatus === FS.SUCCESS && Object.keys(data).length > 0 && (
        <CssGroupButton
          config={[
            { label: "Monitoring", onClick: () => history.push(`/pool-monitoring/${pool}`) },
            { label: "Request", onClick: () => history.push(`/pool-monitoring/${pool}?action=pending`) },
          ]}
          defaultActive={action == "pending" ? 1 : 0}
          style={{ marginTop: "1rem" }}
        />
      )}
      <PoolListPopover id={id} open={open} anchorEl={anchorEl} onClose={handleClose} poolList={poolList} />
    </>
  );
}
